import Konva from 'konva';
import { Box } from 'konva/lib/shapes/Transformer';
import React, { useEffect, useRef, useState } from 'react';
import { Group, Label, Tag, Text, Transformer } from 'react-konva';
import { hideShapeToolBoxes, showShapeToolBoxes } from '../../../utils/toolbox';
import { SELECTION_STROKE_COLOR } from '../defines';

interface ShapeTransformerProps extends Konva.TransformerConfig {
  boundBoxFunc?: ((oldBox: Box, newBox: Box) => Box) | undefined;
}

export const ShapeTransformer = React.forwardRef<
  Konva.Transformer,
  ShapeTransformerProps
>((props, ref) => {
  const transformerRef =
    (ref as React.RefObject<Konva.Transformer>) ||
    useRef<Konva.Transformer>(null);
  const initialRotation = useRef<number>(0);
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
    rotation: number;
    x: number;
    y: number;
    isRotating: boolean;
  } | null>(null);

  const [scale, setScale] = useState(1);

  useEffect(() => {
    const transformer = transformerRef.current;
    if (!transformer) return;

    const stage = transformer.getStage();
    if (!stage) return;

    const handleTransformStart = () => {
      hideShapeToolBoxes();
      const nodes = transformer.nodes();
      if (nodes.length > 0) {
        initialRotation.current = nodes[0].rotation();
      }
    };

    const updateDimensions = () => {
      const nodes = transformer.nodes();
      if (nodes.length === 0) {
        setDimensions(null);
        return;
      }

      const box = transformer.getClientRect();
      const parent = transformer.getParent();
      const invertedParentTransform = parent
        ?.getAbsoluteTransform()
        .copy()
        .invert();
      const topLeft = invertedParentTransform?.point({ x: box.x, y: box.y });

      const scaleX = stage.scaleX();
      const scaleY = stage.scaleY();
      setScale(Math.min(scaleX, scaleY));

      const rotation = nodes[0].rotation();

      setDimensions({
        width: Math.round(box.width / scaleX),
        height: Math.round(box.height / scaleY),
        rotation: Math.round(rotation),
        x: topLeft?.x || 0,
        y: topLeft?.y || 0,
        isRotating: Math.round(rotation) !== Math.round(initialRotation.current)
      });
    };

    const handleTransformEnd = () => {
      setDimensions(null);
      showShapeToolBoxes();
    };

    transformer.on('transformstart', handleTransformStart);
    transformer.on('transform', updateDimensions);
    transformer.on('transformend', handleTransformEnd);

    return () => {
      transformer.off('transformstart', handleTransformStart);
      transformer.off('transform', updateDimensions);
      transformer.off('transformend', handleTransformEnd);
    };
  }, [transformerRef]);

  const fontSize = Math.max(12 / scale, 12);

  const { resizeEnabled = true, rotateEnabled = true, ...rest } = props;

  return (
    <Group>
      <Transformer
        ref={transformerRef}
        borderStroke={SELECTION_STROKE_COLOR}
        borderStrokeWidth={0.5}
        anchorStroke={SELECTION_STROKE_COLOR}
        anchorStrokeWidth={1}
        anchorSize={6}
        anchorCornerRadius={0}
        padding={1}
        enabledAnchors={[
          'top-left',
          'top-right',
          'bottom-left',
          'bottom-right',
          'middle-left',
          'middle-right',
          'bottom-center'
        ]}
        resizeEnabled={resizeEnabled}
        rotateAnchorOffset={20}
        rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
        rotateEnabled={rotateEnabled}
        rotationSnapTolerance={5}
        {...rest}
      />

      {dimensions && (
        <>
          <Label
            x={dimensions.x + dimensions.width / 2}
            y={dimensions.y - 45 / scale}
            offsetX={30 / scale}
          >
            <Tag fill="black" opacity={0.75} cornerRadius={3 / scale} />
            <Text
              text={`W: ${dimensions.width}px`}
              fontSize={fontSize}
              fill="white"
              padding={5 / scale}
              align="center"
            />
          </Label>

          <Label
            x={dimensions.x + dimensions.width + 10 / scale}
            y={dimensions.y + dimensions.height / 2}
            offsetY={10 / scale}
          >
            <Tag fill="black" opacity={0.75} cornerRadius={3 / scale} />
            <Text
              text={`H: ${dimensions.height}px`}
              fontSize={fontSize}
              fill="white"
              padding={5 / scale}
              align="center"
            />
          </Label>

          {dimensions.isRotating && (
            <Label
              x={dimensions.x + dimensions.width / 2}
              y={dimensions.y - 70 / scale}
              offsetX={35 / scale}
            >
              <Tag fill="black" opacity={0.75} cornerRadius={3 / scale} />
              <Text
                text={`R: ${dimensions.rotation}°`}
                fontSize={fontSize}
                fill="white"
                padding={5 / scale}
                align="center"
              />
            </Label>
          )}
        </>
      )}
    </Group>
  );
});
